import { useQueryClient } from "@tanstack/react-query";
import { EmpresaType } from "data/empresa";
import {
  createContext,
  PropsWithChildren,
  useContext,
  useState,
  useEffect,
} from "react";
import { useUser } from "services/account";

interface EmpresaContextInterface {
  empresas: EmpresaType[];
  isLoading: boolean;
  selectedIds: string[];
  selectEmpresas: (ids: string[]) => void;
}


export const EmpresaContext = createContext({} as EmpresaContextInterface);

const EmpresaProvider = ({ children }: PropsWithChildren) => {
  const { user, isLoading } = useUser();
  const client = useQueryClient();

  const empresas = user?.empresas;

  // initialize selected IDs from sessionStorage
  const [selectedIds, setSelectedIds] = useState<string[]>(() => {
    try {
      const stored = sessionStorage.getItem("selectedEmpresas");
      const storedIds = stored ? JSON.parse(stored) : [];

      // Se não houver empresas selecionadas, será configurado após o carregamento de dados
      return storedIds;
    } catch {
      return [];
    }
  });

  // Garantir que ao menos uma empresa está selecionada após o carregamento de dados
  useEffect(() => {
    if (!isLoading && empresas && empresas.length > 0) {
      if (selectedIds.length === 0) {
        // Se não houver seleção, selecione a primeira empresa (considerando a local)
        const localEmpresa = empresas.find((e, i) => e.localDeTrabalho);
        if (localEmpresa) {
          setSelectedIds([localEmpresa.id]);
        }
      }
    }
  }, [isLoading, empresas, selectedIds]);

  // persist selection
  useEffect(() => {
    sessionStorage.setItem("selectedEmpresas", JSON.stringify(selectedIds));
  }, [selectedIds]);

  useEffect(() => {
    client.invalidateQueries();
  }, [selectedIds]);

  const selectEmpresas = (ids: string[]) => {
    // Não permitir um array vazio de empresas selecionadas
    if (ids.length === 0 && empresas && empresas.length > 0) {
      // Se não houver seleção, selecione a primeira empresa (considerando a local)
      const localEmpresa = empresas.find((e, i) => e.localDeTrabalho);
      if (localEmpresa) {
        setSelectedIds([localEmpresa.id]);
      }
    } else {
      setSelectedIds(ids);
    }
  };

  return (
    <EmpresaContext.Provider
      value={{
        isLoading,
        empresas: empresas || ([] as any),
        selectedIds,
        selectEmpresas,
      }}
    >
      {children}
    </EmpresaContext.Provider>
  );
};

export const useEmpresas = () => {
  const context = useContext(EmpresaContext);
  if (!context) {
    throw new Error("useEmpresas must be used within a EmpresaProvider");
  }

  return {
    ...context,
  };
};

export default EmpresaProvider;
